import { Logger } from '@moment/utils/srv-logger.ts';
import { ICache } from './cache.ts';
import {
  deserializeResponse,
  generateCacheKey,
  serializeResponse,
} from './fetcher-cache-utils.ts';
import { LRUCache } from '@moment/net/common/cache-local.ts';

export interface IFetcherOptions extends RequestInit {
  logger?: Logger;
  // You can add other custom options relevant to your fetching logic here
  // For example, options specific to cache key generation or cache behavior
  cacheKeyData?: Record<string, any>; // Data to help generate a unique cache key
  cacheTTL?: number; // Specific TTL for this request if caching
}

export interface IFetcher {
  fetch(
    url: string | URL | Request,
    options?: IFetcherOptions
  ): Promise<Response>;
}

export class DirectFetcher implements IFetcher {
  public async fetch(
    url: string | URL | Request,
    options?: IFetcherOptions
  ): Promise<Response> {
    const { logger, ...fetchOptions } = options || {};
    logger?.log(`DirectFetcher: Fetching ${url.toString()}`);
    // Native fetch doesn't understand custom options like 'logger'
    // so we only pass standard RequestInit options.
    return fetch(url, fetchOptions as RequestInit);
  }
}

export class CacheBackedFetcher implements IFetcher {
  constructor(
    private readonly cache: ICache,
    private readonly fallbackFetcher: IFetcher // e.g., DirectFetcher
  ) {}

  public async fetch(
    url: string | URL | Request,
    options?: IFetcherOptions
  ): Promise<Response> {
    const { logger, cacheTTL, ...fetchOptions } = options || {};
    const key = generateCacheKey(url, options);

    logger?.log(
      `CachedFetcher: Attempting to fetch for key ${key} (URL: ${url.toString()})`
    );

    try {
      const cachedData = await this.cache.get(key);
      if (cachedData) {
        logger?.log(`CachedFetcher: Cache HIT for key ${key}`);
        return deserializeResponse(cachedData);
      }
    } catch (error) {
      logger?.error(
        `CachedFetcher: Error reading from cache for key ${key}`,
        error
      );
      // Proceed to fetch from origin if cache read fails
    }

    logger?.log(
      `CachedFetcher: Cache MISS for key ${key}. Fetching from origin.`
    );
    const response = await this.fallbackFetcher.fetch(url, options);

    if (response.ok) {
      try {
        // Default to the TTL from options, which might be undefined.
        let finalCacheTTL = cacheTTL;

        // Clone the response to safely read its body for the TTL without
        // consuming the stream meant for the end-user.
        const responseForTtlCheck = response.clone();
        const outerResponse = response.clone();

        try {
          const data = await responseForTtlCheck.json();
          const ttlFromResponse = calculateTTLInSecondsFromDate(data?.ttl);

          if (ttlFromResponse !== undefined) {
            finalCacheTTL = ttlFromResponse * 1000;
            logger?.log(
              `CachedFetcher: Using TTL from response body for key ${key}: ${finalCacheTTL}ms`
            );
          }
        } catch (e) {
          // This is expected if the response is not JSON.
          // We'll just use the default TTL.
          logger?.log(
            `CachedFetcher: Could not extract TTL from response body for key ${key}. Using default TTL.`
          );
        }

        // Serialize the response using another clone and store it in the cache
        // with the determined TTL.
        const serializableResponse = await serializeResponse(
          outerResponse.clone()
        );
        await this.cache.set(key, serializableResponse, finalCacheTTL);
        logger?.log(
          `CachedFetcher: Stored response in cache for key ${key} with TTL ${
            finalCacheTTL ? `${finalCacheTTL}ms` : 'default'
          }`
        );
      } catch (error) {
        logger?.error(
          `CachedFetcher: Error writing to cache for key ${key}`,
          error
        );
      }
    } else {
      logger?.log(
        `CachedFetcher: Response not OK (status: ${response.status}). Not caching for key ${key}.`
      );
    }

    return response; // Return the original response from fallback
  }
}

export class MomentFetcher implements IFetcher {
  private isBrowser: boolean;

  constructor(
    private readonly cache: ICache,
    private readonly cachedFetcherInstance: IFetcher, // Instance of CachedFetcher
    private readonly directFetcherInstance: IFetcher // Instance of DirectFetcher
  ) {
    this.isBrowser = !import.meta.env.SSR;
  }

  public async fetch(
    url: string | URL | Request,
    options?: IFetcherOptions
  ): Promise<Response> {
    const logger = options?.logger;

    if (this.isBrowser) {
      logger?.log('SmartFetcher: Running in browser, using DirectFetcher.');
      return this.directFetcherInstance.fetch(url, options);
    }

    // Server-side logic
    try {
      const cacheAvailable = await this.cache.isAvailable();
      if (cacheAvailable) {
        logger?.log('SmartFetcher: Cache is available, using CachedFetcher.');
        return this.cachedFetcherInstance.fetch(url, options);
      } else {
        logger?.log(
          'SmartFetcher: Cache is NOT available, using DirectFetcher.'
        );
        return this.directFetcherInstance.fetch(url, options);
      }
    } catch (error) {
      logger?.error(
        'SmartFetcher: Error checking cache availability or during fetch. Falling back to DirectFetcher.',
        error
      );
      return this.directFetcherInstance.fetch(url, options);
    }
  }
}

/**
 * Calculates a TTL in seconds from an ISO 8601 date string.
 * @param ttlDateString The expiry date string from the API.
 * @returns The number of seconds until expiry, or undefined if the date is invalid or in the past.
 */
export const calculateTTLInSecondsFromDate = (
  ttlDateString: unknown
): number | undefined => {
  if (typeof ttlDateString !== 'string') {
    return undefined;
  }

  const expiryDate = new Date(ttlDateString);
  // Check if the parsed date is valid
  if (isNaN(expiryDate.getTime())) {
    return undefined;
  }

  const now = new Date();
  const ttlInSeconds = Math.round(
    (expiryDate.getTime() - now.getTime()) / 1000
  );

  // Only return a positive TTL
  return ttlInSeconds > 0 ? ttlInSeconds : undefined;
};

export const createMomentFetcher = () => {
  const directFetcher = new DirectFetcher();
  const cache = new LRUCache();
  const cachedFetcher = new CacheBackedFetcher(cache, directFetcher);
  return new MomentFetcher(cache, cachedFetcher, directFetcher);
};
